import React, { FunctionComponent, useState, MouseEvent } from 'react';
import { camelCase } from 'lodash';
import { Paper, makeStyles, Box } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import Button from '@material-ui/core/Button';
import Modal from '../../../../components/Modal';
import Loader from '../../../../components/Loader';
import DeleteDialog from '../../../../components/DeleteDialog';
import PersonContactDialog from '../../Tabs/PersonContactDialog';
import { Person, Contact } from '../../../../store/modules/person/types';
import TableEmptyState from '../../../../components/TableView/TableEmptyState';
import InfiniteScrollTableView from '../../../../components/InfiniteScrollTableView';
import { getColumnFormat, getFlatColumnFieldsFromList, getFullAddress } from '../../../../utils/utils';
import { ConfiguredFields, ConfiguredFieldTypes, IColumn, Order } from 'src/store/modules/application/types';

type PersonContactsProps = {
    person: Person;
    loading?: boolean;
    personContactList: Contact[];
    deleteContact: Function;
    skip: number;
    setSkip: Function;
    hasMore: boolean;
    personContactsCount: number;
    sortBy: string;
    sortOrder: Order;
    setSortOrder: Function;
    setSortColumn: Function;
    contactColumnsFields: ConfiguredFields;
};

const useStyles = makeStyles({
    noDataResultContainer: {
        width: '100%',
        maxHeight: 300,
        color: 'gray',
        paddingTop: '40px',
        paddingBottom: '150px',
    },
    noResultFound: {
        fontSize: '24px',
        color: '#484848',
        lineHeight: '24px',
        fontWeight: 600,
        marginTop: '3rem',
        textAlign: 'center',
    },
    noResultFoundMessage2: {
        fontSize: '18px',
        color: '#484848',
        lineHeight: '24px',
        marginTop: '20px',
        fontWeight: 500,
        textAlign: 'center',
    },
    noResultFoundFileType: {
        fontSize: '16px',
        color: '#6E7788',
        lineHeight: '24px',
        fontWeight: 500,
        textAlign: 'center',
    },
    noResultFoundUploadFile: {
        fontSize: '18px',
        color: '#0277BD',
        lineHeight: '24px',
        fontWeight: 500,
        textAlign: 'center',
        marginTop: '20px',
        cursor: 'pointer',
    },
    addContact: {
        color: '#0277BD',
        marginRight: '40px',
        fontWeight: 600,
        cursor: 'pointer',
        letterSpacing: '0.3px,',
        display: 'flex',
        alignItems: 'center',
    },
    contactContainer: {
        display: 'flex',
        justifyContent: 'flex-end',
    },
    tablePaginationInfo: {
        display: 'flex',
        justifyContent: 'space-between',
        padding: '10px',
    },
    countSpan: {
        color: '#484848',
        fontSize: 14,
        letterSpacing: 0.1,
        display: 'flex',
        alignItems: 'center',
    },
});

const PersonContacts: FunctionComponent<PersonContactsProps> = (props) => {
    const {
        person,
        loading,
        personContactList,
        deleteContact,
        hasMore,
        skip,
        setSkip,
        setSortColumn,
        setSortOrder,
        sortBy,
        sortOrder,
        personContactsCount,
        contactColumnsFields,
    } = props;

    const [isModalOpen, setModalStatus] = useState<boolean>(false);
    const [isEditModalOpen, setEditModalStatus] = useState<boolean>(false);
    const [selectedPersonContact, setSelectedPersonContactDetail] = useState<Contact>();
    const [contactId, setContactId] = useState<string>();
    const [isDeleteModalOpen, setModal] = useState(false);
    const personId = person.id;

    const columns: IColumn[] = contactColumnsFields?.fields?.length
        ? contactColumnsFields?.fields?.map((personContactColumn: ConfiguredFieldTypes) => {
              return {
                  id: `${camelCase(personContactColumn.name)}|${getColumnFormat(personContactColumn.dataType)}`,
                  label: personContactColumn.name,
                  // Need to slice first two characters '$.' in order to match column data field
                  field: personContactColumn.data.slice(2),
                  format: personContactColumn.format,
              };
          })
        : [
              { id: 'contactType|string', label: 'Contact Type', maxWidth: 150, field: 'contactType', noSort: true },
              { id: 'name|string', label: 'Name', maxWidth: 300, field: 'name', noSort: true },
              {
                  id: 'company|string',
                  label: 'Company',
                  align: 'left',
                  maxWidth: 200,
                  field: 'company',
                  noSort: true,
              },
              {
                  id: 'address|string',
                  label: 'Address',
                  align: 'left',
                  maxWidth: 300,
                  field: 'address',
                  noSort: true,
              },
              {
                  id: 'phone|string',
                  label: 'Phone',
                  align: 'left',
                  maxWidth: 150,
                  field: 'phone',
                  noSort: true,
              },
              {
                  id: 'fax|string',
                  label: 'Fax',
                  align: 'left',
                  maxWidth: 150,
                  field: 'fax',
                  noSort: true,
              },
              {
                  id: 'email|string',
                  label: 'Email',
                  align: 'left',
                  maxWidth: 200,
                  field: 'email',
                  noSort: true,
              },
          ];

    const contactList = contactColumnsFields?.fields?.length
        ? getFlatColumnFieldsFromList(personContactList, columns)
        : personContactList?.map((contact) => {
              return {
                  ...contact,
                  contactType: contact.contactType?.name,
                  address: getFullAddress(contact?.address),
              };
          });

    const classes = useStyles();

    /* Handle delete case button */
    const handleDeleteCase = (id: string, e: any) => {
        e.stopPropagation();
        setContactId(id);
        setModal(true);
    };
    const onClose = () => {
        setModal(false);
    };
    const onDelete = () => {
        setModal(false);
        deleteContact(contactId, personId);
    };

    const onClickRow = (id: string, e: any) => {
        e.stopPropagation();
        const selectedPersonContact = personContactList?.find((person) => person.id === id);
        setEditModalStatus(true);
        setModalStatus(true);
        setSelectedPersonContactDetail(selectedPersonContact);
    };

    const handleAddContact = (e: MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        setEditModalStatus(false);
        setModalStatus(true);
        setSelectedPersonContactDetail(undefined);
    };

    const handleModalStatus = (status: boolean) => {
        setModalStatus(status);
    };

    const loadMore = () => {
        if (hasMore) {
            setSkip(skip + 1);
        }
    };

    if (loading) return <Loader />;

    return (
        <div className="verticalFillContainer">
            <div className={classes.contactContainer}>
                <Button
                    startIcon={<AddIcon />}
                    className={classes.addContact}
                    onClick={(e: MouseEvent<HTMLElement>) => {
                        handleAddContact(e);
                    }}
                >
                    ADD Contact
                </Button>
            </div>
            <Box className={classes.tablePaginationInfo}>
                <span className={classes.countSpan}>{`${personContactsCount} Results`}</span>
            </Box>
            <Paper className="case-entries-container verticalFillContent">
                {contactList && contactList.length > 0 ? (
                    <InfiniteScrollTableView
                        columns={columns}
                        data={contactList!}
                        count={contactList.length}
                        onClickRow={onClickRow}
                        deleteFlag={true}
                        handleDeleteCase={handleDeleteCase}
                        hasMore={hasMore}
                        loadMore={loadMore}
                        sortBy={sortBy}
                        sortOrder={sortOrder}
                        setSortColumn={setSortColumn}
                        setSortOrder={setSortOrder}
                    />
                ) : (
                    <TableEmptyState
                        title="No active records for this license."
                        message=" All your contacts will be stored here "
                    ></TableEmptyState>
                )}
                {/* Modal for Add Contact */}
                {isModalOpen && (
                    <Modal open={isModalOpen} maxWidth={'sm'} fullWidth={true}>
                        <PersonContactDialog
                            setModalStatus={handleModalStatus}
                            personId={person.id}
                            isEditModalOpen={isEditModalOpen}
                            selectedPersonContactDetail={selectedPersonContact!}
                        />
                    </Modal>
                )}

                {isDeleteModalOpen && (
                    <Modal open={true} maxWidth={'md'} fullWidth={true} minHeight={'10vh'}>
                        {isDeleteModalOpen && (
                            <DeleteDialog
                                onClose={onClose}
                                onDelete={onDelete}
                                contentTitle="Are you sure you'd like to proceed with this action?"
                                content=""
                            />
                        )}
                    </Modal>
                )}
            </Paper>
        </div>
    );
};

export default PersonContacts;
